import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/authContext';
import { adminVideosAPI } from '../utils/api/adminAPI';
import { Toast } from '../components/Auth/Toast';
import { adminNavigationItems as navigationItems } from '../components/admin/adminNavigation';
import './AdminDashboard.css';

const quickActions = [
  { key: 'add-news', title: 'Create News', summary: 'Publish a new headline or update the feed.' },
  { key: 'home-newss', title: 'HomeNewss', summary: 'Manage homepage section-wise stories.' },
  { key: 'videos', title: 'Review Videos', summary: 'Check recent uploads and update metadata.' },
  { key: 'breaking-headlines', title: 'Update Ticker', summary: 'Refresh urgent headlines shown on the site.' },
  { key: 'manage-villages', title: 'Manage Villages', summary: 'Keep village information and coverage clean.' },
  { key: 'manage-theme', title: 'Control Theme', summary: 'Update website-wide colors from one panel.' }
];

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not admin
    if (user && user.role !== 'admin') {
      navigate('/');
    }

    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminVideosAPI.getDashboardStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      setToast({
        type: 'error',
        message: '❌ Failed to load dashboard stats'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsSidebarOpen(false);
    logout();
    navigate('/');
    setToast({
      type: 'success',
      message: '✅ Logged out successfully'
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsSidebarOpen(false);

    if (tab === 'videos') {
      navigate('/admin/videos');
    } else if (tab === 'add-news') {
      navigate('/admin/news/add');
    } else if (tab === 'home-newss') {
      navigate('/admin/home-newss');
    } else if (tab === 'other-news') {
      navigate('/admin/other-news');
    } else if (tab === 'breaking-headlines') {
      navigate('/admin/breaking-headlines');
    } else if (tab === 'manage-theme') {
      navigate('/admin/theme');
    } else if (tab === 'chitrakoot-blocks') {
      navigate('/admin/chitrakoot-blocks');
    } else if (tab === 'manage-districts') {
      navigate('/admin/manage-districts');
    } else if (tab === 'manage-villages') {
      navigate('/admin/manage-villages');
    }
  };

  const recentVideos = stats?.recentVideos || [];
  const categoryEntries = Object.entries(stats?.categoryCount || {}).sort((first, second) => second[1] - first[1]);
  const topCategory = categoryEntries[0];
  const totalVideos = stats?.totalVideos || 0;
  const totalCategories = stats?.totalCategories || 0;
  const averageVideosPerCategory = totalCategories > 0 ? (totalVideos / totalCategories).toFixed(1) : '0.0';

  const formatDate = (value) => new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));

  return (
    <div className="admin-dashboard-container">
      <div
        className={`admin-sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar Navigation */}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'active' : ''}`}>
        <div className="admin-sidebar-header">
          <span className="admin-sidebar-kicker">Latthsaab Network</span>
          <h1 className="admin-sidebar-title">Admin Control</h1>
          <p className="admin-sidebar-subtitle">Operations panel for editorial and media management</p>
        </div>

        <nav className="admin-nav">
          {navigationItems.map((item) => (
            <button
              key={item.key}
              className={`admin-nav-item ${activeTab === item.key ? 'active' : ''}`}
              onClick={() => handleTabChange(item.key)}
            >
              <span className="nav-icon">{item.accent}</span>
              <span className="nav-copy">
                <span className="nav-label">{item.label}</span>
                <span className="nav-description">{item.description}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <div className="user-avatar">{(user?.name || 'A').charAt(0).toUpperCase()}</div>
            <div className="user-details">
              <p className="user-name">{user?.name || 'Admin'}</p>
              <p className="user-email">{user?.email || 'admin@example.com'}</p>
            </div>
          </div>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main-content">
        {/* Top Header */}
        <header className="admin-top-header">
          <div className="admin-header-left">
            <button
              type="button"
              className="admin-menu-toggle"
              onClick={() => setIsSidebarOpen((open) => !open)}
              aria-label="Toggle dashboard navigation"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div>
              <p className="admin-page-eyebrow">Operations Dashboard</p>
              <h2 className="admin-page-title">Content and distribution overview</h2>
            </div>
          </div>
          <div className="admin-header-right">
            <button type="button" className="admin-header-btn secondary" onClick={fetchStats}>
              Refresh data
            </button>
            <button type="button" className="admin-header-btn primary" onClick={() => handleTabChange('add-news')}>
              New story
            </button>
          </div>
        </header>

        {/* Dashboard Content */}
        {activeTab === 'dashboard' && (
          <div className="admin-dashboard-content">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading dashboard stats...</p>
              </div>
            ) : stats ? (
              <>
                <section className="dashboard-hero">
                  <div className="dashboard-hero-copy">
                    <span className="dashboard-section-kicker">Live editorial workspace</span>
                    <h3 className="dashboard-hero-title">Welcome back, {user?.name || 'Admin'}.</h3>
                    <p className="dashboard-hero-text">
                      Track upload volume, monitor category mix, and jump straight into the most common admin actions from one place.
                    </p>
                    <div className="dashboard-hero-actions">
                      <button type="button" className="hero-action primary" onClick={() => handleTabChange('videos')}>
                        Open video manager
                      </button>
                      <button type="button" className="hero-action secondary" onClick={() => handleTabChange('breaking-headlines')}>
                        Edit breaking ticker
                      </button>
                    </div>
                  </div>

                  <div className="dashboard-hero-panel">
                    <div className="hero-panel-label">Publishing health</div>
                    <div className="hero-panel-value">{topCategory ? `${Math.round((topCategory[1] / Math.max(totalVideos, 1)) * 100)}%` : '0%'} focus</div>
                    <p className="hero-panel-text">
                      {topCategory
                        ? `${topCategory[0]} is currently the strongest category in your video library.`
                        : 'Start publishing content to build category insights.'}
                    </p>
                    <div className="hero-panel-metrics">
                      <div>
                        <span>Top category</span>
                        <strong>{topCategory?.[0] || 'No data yet'}</strong>
                      </div>
                      <div>
                        <span>Average volume</span>
                        <strong>{averageVideosPerCategory}</strong>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="stats-grid">
                  <div className="stat-card accent-orange">
                    <div className="stat-icon">VL</div>
                    <div className="stat-content">
                      <h3>Total Video Library</h3>
                      <p className="stat-number">{stats.totalVideos}</p>
                      <span className="stat-meta">All uploaded videos across the platform</span>
                    </div>
                  </div>

                  <div className="stat-card accent-red">
                    <div className="stat-icon">CT</div>
                    <div className="stat-content">
                      <h3>Active Categories</h3>
                      <p className="stat-number">{stats.totalCategories}</p>
                      <span className="stat-meta">Distinct content segments being used</span>
                    </div>
                  </div>

                  <div className="stat-card accent-gold">
                    <div className="stat-icon">RC</div>
                    <div className="stat-content">
                      <h3>Recent Uploads</h3>
                      <p className="stat-number">{stats.recentVideos?.length || 0}</p>
                      <span className="stat-meta">Items added in the latest activity set</span>
                    </div>
                  </div>

                  <div className="stat-card accent-slate">
                    <div className="stat-icon">MX</div>
                    <div className="stat-content">
                      <h3>Top Category Share</h3>
                      <p className="stat-number">
                        {topCategory ? `${Math.round((topCategory[1] / Math.max(totalVideos, 1)) * 100)}%` : '0%'}
                      </p>
                      <span className="stat-meta">Share held by the leading category</span>
                    </div>
                  </div>
                </div>

                <section className="dashboard-strip">
                  <div className="dashboard-section quick-actions-section">
                    <div className="section-heading-row">
                      <div>
                        <span className="dashboard-section-kicker">Shortcuts</span>
                        <h3 className="section-title">Quick actions</h3>
                      </div>
                    </div>

                    <div className="quick-actions-grid">
                      {quickActions.map((action) => (
                        <button
                          key={action.key}
                          type="button"
                          className="quick-action-card"
                          onClick={() => handleTabChange(action.key)}
                        >
                          <span className="quick-action-label">{action.title}</span>
                          <span className="quick-action-summary">{action.summary}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="dashboard-section summary-card">
                    <span className="dashboard-section-kicker">Publishing summary</span>
                    <h3 className="section-title">Operational snapshot</h3>
                    <div className="summary-list">
                      <div className="summary-item">
                        <span>Top performing segment</span>
                        <strong>{topCategory ? `${topCategory[0]} (${topCategory[1]})` : 'No data yet'}</strong>
                      </div>
                      <div className="summary-item">
                        <span>Average videos per category</span>
                        <strong>{averageVideosPerCategory}</strong>
                      </div>
                      <div className="summary-item">
                        <span>Latest update window</span>
                        <strong>{recentVideos.length ? `${recentVideos.length} recent uploads tracked` : 'No recent uploads'}</strong>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="dashboard-grid">
                  <div className="dashboard-section category-section">
                    <div className="section-heading-row">
                      <div>
                        <span className="dashboard-section-kicker">Distribution</span>
                        <h3 className="section-title">Videos by category</h3>
                      </div>
                      <span className="section-side-note">Sorted by volume</span>
                    </div>

                    <div className="category-breakdown">
                      {categoryEntries.length > 0 ? (
                        categoryEntries.map(([category, count], index) => (
                          <div key={category} className="category-item">
                            <div className="category-rank">{String(index + 1).padStart(2, '0')}</div>
                            <div className="category-main">
                              <div className="category-row">
                                <span className="category-name">{category}</span>
                                <span className="category-share">{Math.round((count / Math.max(totalVideos, 1)) * 100)}%</span>
                              </div>
                              <div className="category-bar-container">
                                <div
                                  className="category-bar"
                                  style={{ width: `${(count / Math.max(totalVideos, 1)) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                            <span className="category-count">{count}</span>
                          </div>
                        ))
                      ) : (
                        <p className="no-data">No category data available yet.</p>
                      )}
                    </div>
                  </div>

                  <div className="dashboard-section recent-section">
                    <div className="section-heading-row">
                      <div>
                        <span className="dashboard-section-kicker">Recent activity</span>
                        <h3 className="section-title">Recently uploaded videos</h3>
                      </div>
                      <button type="button" className="inline-action" onClick={() => handleTabChange('videos')}>
                        View all videos
                      </button>
                    </div>

                    <div className="recent-videos-list">
                      {recentVideos.length > 0 ? (
                        recentVideos.map((video, index) => (
                          <div key={video._id} className="recent-video-item">
                            <div className="recent-video-index">{String(index + 1).padStart(2, '0')}</div>
                            <div className="video-info">
                              <h4>{video.title}</h4>
                              <div className="video-meta-row">
                                <p className="video-category">{video.category}</p>
                                <p className="video-date">{formatDate(video.createdAt)}</p>
                              </div>
                            </div>
                            <button className="view-btn" onClick={() => handleTabChange('videos')}>
                              Edit entry
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="no-data">No videos uploaded yet.</p>
                      )}
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <div className="error-container">
                <p>Failed to load dashboard data</p>
                <button type="button" className="admin-header-btn primary retry-btn" onClick={fetchStats}>
                  Try again
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
